import { Check, LoaderCircle, type LucideIcon, Minus, Volume2, VolumeX } from 'lucide-react'
import { type ReactNode, useId } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import { Pill } from '../../controls/Pill'
import { formatOffset, formatShortDate } from '../../format'
import { SubjectPortrait } from '../../home/SubjectPortrait'
import { BRACKET_CORNERS, MICRO_LABEL, MONO, ON_PRIMARY, TEXT_CAPTION } from '../../recipes'
import { SubjectLabel } from '../../SubjectLabel'
import { VoiceprintTag } from '../VoiceprintTag'
import { VoiceQuality } from '../VoiceQuality'
import { wholeSeconds } from '../voice-format'
import { playedShare } from '../voiceprint-format'
import { WaveStrip } from '../WaveStrip'
import type { VoicePairSideState } from './voice-pair-model'
import type { VoicePairSide, VoicePairSideId } from './voice-pair-types'

/** A recording/reference source carries its own heading instead of a voiceprint owner. */
export type VoiceListeningSide =
  | VoicePairSide
  | { waveform: readonly number[] | null; source: ReactNode }

export interface VoicePairChannelProps {
  id: VoicePairSideId
  /** Whose voiceprint it is and what it was made from. */
  side: VoiceListeningSide
  /** How its clip stands under the transport. */
  state: VoicePairSideState
  /** Whether this side is the one in the transport, and how its playhead is moved. */
  control: { active: boolean; onSeek: (ms: number) => void }
}

/** The side's letter: filled for the side in the transport, an outline for the other. */
function SideLetter({ id, active }: { id: VoicePairSideId; active: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        MONO,
        'flex size-7 shrink-0 items-center justify-center rounded-md border text-sm font-bold uppercase',
        active ? cn('border-primary bg-primary', ON_PRIMARY) : 'border-border text-muted-foreground'
      )}
    >
      {id}
    </span>
  )
}

/** Whose voice it is, the voiceprint as a tag, and what it was made from: when, how much speech. */
function ChannelSource({ side }: { side: VoicePairSide }) {
  const { t, i18n } = useTranslation()
  const { voiceprint, owner } = side
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex min-w-0 items-center gap-3">
        <SubjectPortrait
          url={owner.portraitUrl}
          className="size-10 shrink-0 rounded-full border border-border"
        />
        {/* In a narrow column the tag goes under the name before the name is cut. */}
        <SubjectLabel
          subject={owner}
          order="name-first"
          className="flex-wrap gap-y-0 text-sm font-semibold @3xl/voicepair:text-[15px]"
        />
      </div>
      <VoiceprintTag voiceprint={voiceprint} />
      <p className={cn(TEXT_CAPTION, 'flex flex-wrap gap-x-1.5 text-muted-foreground')}>
        {voiceprint.createdAt && (
          <span className={MONO}>{formatShortDate(voiceprint.createdAt, i18n.language)}</span>
        )}
        {voiceprint.createdAt && voiceprint.speechMs !== null && <span aria-hidden>·</span>}
        {voiceprint.speechMs !== null && (
          <span>{t('humint.voice.mark.speech', { count: wholeSeconds(voiceprint.speechMs) })}</span>
        )}
      </p>
      <VoiceQuality quality={voiceprint.quality} />
    </div>
  )
}

type ChannelWaveProps = VoicePairChannelProps

/**
 * The side's clip as bars with its played part, over a native range input: the playhead is
 * moved by a tap, by the keyboard and by a screen reader alike. A side with nothing to play is
 * dimmed and holds still.
 */
function ChannelWave({ id, side, state, control }: ChannelWaveProps) {
  const { t } = useTranslation()
  const { active, onSeek } = control
  const { positionMs, durationMs } = state.playback
  const still = state.audio === 'unavailable' || durationMs <= 0
  const played = playedShare(positionMs, durationMs)
  return (
    <div className={cn('relative', still && 'opacity-50')}>
      <input
        type="range"
        min={0}
        max={durationMs}
        step={1000}
        value={positionMs}
        disabled={still}
        onChange={event => onSeek(Number(event.target.value))}
        aria-label={t('humint.voice.match.transport.seek', {
          side: t(`humint.voice.match.side.${id}`),
        })}
        aria-valuetext={t('humint.questioning.player.position', {
          position: formatOffset(positionMs),
          duration: formatOffset(durationMs),
        })}
        className="peer absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0 disabled:cursor-default"
      />
      <WaveStrip
        peaks={side.waveform}
        played={played}
        className="h-12 rounded-md peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-card"
      />
      {/* The playhead: where this side stands, also before anything of it was played. */}
      {!still && (active || positionMs > 0) && (
        <span
          aria-hidden
          data-playhead
          style={{ left: `${played * 100}%` }}
          className={cn(
            'pointer-events-none absolute -top-1 -bottom-1 w-0.5 -translate-x-1/2 rounded-full',
            active ? 'bg-foreground' : 'bg-muted-foreground'
          )}
        />
      )}
    </div>
  )
}

interface StatusLook {
  Icon: LucideIcon
  text: string
  spin?: boolean
  warning?: boolean
}

/** What the side's last line says: its clip is on its way, cannot be played, was heard or not. */
function statusLook(state: VoicePairSideState): StatusLook {
  if (state.audio === 'loading') {
    return { Icon: LoaderCircle, text: 'humint.voice.match.audio.loading', spin: true }
  }
  if (state.audio === 'unavailable') {
    return { Icon: VolumeX, text: 'humint.voice.recording.audition.unavailable', warning: true }
  }
  return state.heard
    ? { Icon: Check, text: 'humint.voice.match.listened.heard' }
    : { Icon: Minus, text: 'humint.voice.match.listened.notHeard' }
}

/** "00:07 / 00:17" and, beside it, how the side stands: an icon and words, never colour alone. */
function ChannelFoot({
  id,
  state,
  active,
}: Pick<VoicePairChannelProps, 'id' | 'state'> & { active: boolean }) {
  const { t } = useTranslation()
  const { Icon, text, spin, warning } = statusLook(state)
  const { positionMs, durationMs } = state.playback
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
      {/* A side with no clip has no length to count against. */}
      <p className={cn(MONO, 'text-xs text-muted-foreground', durationMs <= 0 && 'invisible')}>
        <span className={cn('font-semibold', active && 'text-foreground')}>
          {formatOffset(positionMs)}
        </span>{' '}
        / {formatOffset(durationMs)}
      </p>
      <p
        data-side-status={
          state.audio === 'ready' || state.audio === 'none' ? String(state.heard) : state.audio
        }
        className={cn(
          TEXT_CAPTION,
          'flex items-center gap-1.5 font-medium',
          warning ? 'text-warning-foreground' : 'text-muted-foreground'
        )}
      >
        <Icon
          aria-hidden
          className={cn('size-3.5 shrink-0', spin && 'animate-spin motion-reduce:animate-none')}
        />
        {t(text, { side: t(`humint.voice.match.side.${id}`) })}
      </p>
    </div>
  )
}

/**
 * One side of the comparison: whose voice, its voiceprint as a small tag, and its clip with the
 * played part. The clip, not the mark, is the centre of a side — two marks are never set against
 * each other as pictures. The side in the transport is framed by the copper brackets and its
 * letter is filled, so it is told apart by shape as well as by tone; while it sounds it says so.
 */
export function VoicePairChannel({ id, side, state, control }: VoicePairChannelProps) {
  const { t } = useTranslation()
  const headingId = useId()
  const { active } = control
  return (
    <section
      aria-labelledby={headingId}
      aria-current={active ? 'true' : undefined}
      data-side={id}
      className={cn(
        'relative flex min-w-0 flex-col gap-3 rounded-xl border p-4 transition-colors duration-150 motion-reduce:transition-none',
        active ? 'border-primary/40 bg-primary/5' : 'border-border bg-background'
      )}
    >
      {/* The brackets close in on the side taken into the transport: the one beat of a switch. */}
      {active && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-1.5 animate-in duration-200 fade-in-0 zoom-in-105 motion-reduce:animate-none"
        >
          {BRACKET_CORNERS.map(corner => (
            <span key={corner} className={cn('absolute size-3.5 border-primary', corner)} />
          ))}
        </span>
      )}
      <header className="flex min-h-7 items-center justify-between gap-2">
        <h4 id={headingId} className="flex items-center gap-2">
          <SideLetter id={id} active={active} />
          <span className={MICRO_LABEL}>{t(`humint.voice.match.side.${id}`)}</span>
        </h4>
        {/* Only while it really sounds: a side whose clip is still on its way is silent. */}
        {state.playback.playing && state.audio === 'ready' && (
          <Pill tone="copper" size="compact">
            <Volume2 aria-hidden />
            {t('humint.voice.recording.audition.playing')}
          </Pill>
        )}
      </header>
      {'source' in side ? side.source : <ChannelSource side={side} />}
      {/* At the foot of the card: side by side, the two clips line up whatever is written above. */}
      <div className="mt-auto flex flex-col gap-3">
        <ChannelWave id={id} side={side} state={state} control={control} />
        <ChannelFoot id={id} state={state} active={active} />
      </div>
    </section>
  )
}
